/**
 * Hook for fetching and managing KPI Overview data.
 * Extracted from KPIOverviewTab.tsx for better code organization.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchKpiOverview, KpiOverviewRequestBody } from '../services/kpiOverviewApi';
import {
	KpiMetric,
	KpiCategory,
	normalizeFilterToken,
	toNumericToken,
	resolveKpiCategory,
} from '../utils/kpiHelpers';

interface LHDropdownItem {
	id: string | number;
	displayText: string;
	[key: string]: any;
}

interface UseKpiOverviewParams {
	locationOptions: LHDropdownItem[];
	location: LHDropdownItem[];
	year: LHDropdownItem | null;
	period: LHDropdownItem | null;
	weekInYear: LHDropdownItem | null;
	showDashboardContent: boolean;
	draftYearMulti: LHDropdownItem[];
	draftPeriodMulti: LHDropdownItem[];
	draftWeekMulti: LHDropdownItem[];
	/** All available period numbers (fallback when no period is selected) */
	allPeriods?: number[];
	/** All available weekInYear numbers (fallback when no week is selected) */
	allWeekInYears?: number[];
	/** ISO date string (YYYY-MM-DD) — start of date range for API */
	startDate?: string;
	/** ISO date string (YYYY-MM-DD) — end of date range for API */
	endDate?: string;
}

interface UseKpiOverviewReturn {
	metrics: KpiMetric[];
	loading: boolean;
	error: string | null;
	fetchKpis: () => Promise<void>;
	filteredMetrics: KpiMetric[];
	topOpportunities: KpiMetric[];
	biggestWins: KpiMetric[];
	categoryCounts: Record<KpiCategory, number>;
	searchTerm: string;
	setSearchTerm: (term: string) => void;
	selectedCategories: KpiCategory[];
	setSelectedCategories: React.Dispatch<React.SetStateAction<KpiCategory[]>>;
	toggleCategory: (category: KpiCategory) => void;
	locationListToken: string;
	selectedYearToken: string;
	selectedPeriodToken: string;
	selectedWeekToken: string;
	triggerFetch: () => void;
}

export function useKpiOverview({
	locationOptions,
	location,
	year,
	period,
	weekInYear,
	showDashboardContent,
	draftYearMulti,
	draftPeriodMulti,
	draftWeekMulti,
	allPeriods = [],
	allWeekInYears = [],
	startDate,
	endDate,
}: UseKpiOverviewParams): UseKpiOverviewReturn {
	const [metrics, setMetrics] = useState<KpiMetric[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fetchSignal, setFetchSignal] = useState(0);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategories, setSelectedCategories] = useState<KpiCategory[]>([]);

	const selectedLocationToken = useMemo(() => {
		if (location && location.length > 0) {
			return location
				.map(loc => normalizeFilterToken(loc.id))
				.filter(Boolean)
				.join(',');
		}
		return '';
	}, [location]);

	const selectedYearToken = normalizeFilterToken(year?.id);
	const selectedPeriodTokenSingle = toNumericToken(period?.id);
	const selectedWeekTokenSingle = toNumericToken(weekInYear?.id);

	const locationListToken = useMemo(() => {
		if (selectedLocationToken) {
			return selectedLocationToken;
		}
		return locationOptions
			.filter(option => normalizeFilterToken(option.id))
			.map(option => String(option.id))
			.join(',');
	}, [locationOptions, selectedLocationToken]);

	// Derive multi-select tokens
	const selectedYearTokens: string[] = useMemo(() => {
		return draftYearMulti
			.map(y => normalizeFilterToken(y.id))
			.filter(Boolean);
	}, [draftYearMulti]);

	const selectedPeriodTokens: number[] = useMemo(() => {
		return draftPeriodMulti
			.map(p => {
				const token = toNumericToken(p.id);
				return token ? Number(token) : NaN;
			})
			.filter(n => !Number.isNaN(n));
	}, [draftPeriodMulti]);

	const selectedWeekTokens: number[] = useMemo(() => {
		return draftWeekMulti
			.map(w => {
				const token = toNumericToken(w.id);
				return token ? Number(token) : NaN;
			})
			.filter(n => !Number.isNaN(n));
	}, [draftWeekMulti]);

	// Multi-aware tokens: prefer multi-select array joined as comma-separated, fallback to single-select
	const selectedPeriodToken: string = useMemo(() => {
		if (selectedPeriodTokens.length > 0) {
			return selectedPeriodTokens.join(',');
		}
		return selectedPeriodTokenSingle;
	}, [selectedPeriodTokens, selectedPeriodTokenSingle]);

	const selectedWeekToken: string = useMemo(() => {
		if (selectedWeekTokens.length > 0) {
			return selectedWeekTokens.join(',');
		}
		return selectedWeekTokenSingle;
	}, [selectedWeekTokens, selectedWeekTokenSingle]);

	const buildKpiOverviewPayload = useCallback((): KpiOverviewRequestBody => {
		const locationArr = locationListToken
			.split(',')
			.map(token => token.trim())
			.filter(Boolean);

		const payload: KpiOverviewRequestBody = {
			location: locationArr,
			year: selectedYearTokens.length > 0 ? selectedYearTokens : (selectedYearToken ? [selectedYearToken] : []),
		};

		// Period: only send when user has explicitly selected one
		const hasPeriodSelection = selectedPeriodTokens.length > 0 || !!selectedPeriodToken;
		if (selectedPeriodTokens.length > 0) {
			payload.period = selectedPeriodTokens;
		} else if (selectedPeriodToken) {
			const parsedPeriod = Number(selectedPeriodToken);
			if (!Number.isNaN(parsedPeriod)) {
				payload.period = [parsedPeriod];
			}
		}
		// else: no period selected — omit period (backend returns all)

		// Week: only send when user has explicitly selected one.
		// When omitted, the backend scopes to the selected period's weeks automatically.
		if (selectedWeekTokens.length > 0) {
			payload.week = selectedWeekTokens;
		} else if (selectedWeekToken) {
			const parsedWeek = Number(selectedWeekToken);
			if (!Number.isNaN(parsedWeek)) {
				payload.week = [parsedWeek];
			}
		}
		// else: no week selected — omit week (backend handles scoping to period)

		// Date range: include startDate/endDate when available (both or neither)
		if (startDate && endDate) {
			payload.startDate = startDate;
			payload.endDate = endDate;
		}

		return payload;
	}, [locationListToken, selectedYearTokens, selectedYearToken, selectedPeriodTokens, selectedPeriodToken, selectedWeekTokens, selectedWeekToken, allPeriods, allWeekInYears, startDate, endDate]);

	const fetchKpis = useCallback(async () => {
		if (!showDashboardContent) return;

		setLoading(true);
		setError(null);

		try {
			const requestBody = buildKpiOverviewPayload();
			const allowedMetrics = await fetchKpiOverview(requestBody);
			console.log('[KPI DEBUG] Metrics from backend:', allowedMetrics.map(m => ({ name: m.name, category: m.category })));
			// [HIDDEN] SIA metric removed from display per business requirement
			const filteredMetricsResult = allowedMetrics.filter(m => m.name !== 'SIA' && m.name !== 'SIA %');
			setMetrics(filteredMetricsResult);
		} catch (err: any) {
			setError(err?.message || 'Failed to load KPI overview.');
		} finally {
			setLoading(false);
		}
	}, [buildKpiOverviewPayload, showDashboardContent]);

	// Initial mount fetch — omits both period and week (backend returns all)
	const fetchKpisInitial = useCallback(async () => {
		if (!showDashboardContent) return;

		setLoading(true);
		setError(null);

		try {
			const locationArr = locationListToken
				.split(',')
				.map(token => token.trim())
				.filter(Boolean);

			const initialPayload: KpiOverviewRequestBody = {
				location: locationArr,
				year: selectedYearToken ? [selectedYearToken] : [],
			};

			// Do NOT send period or week on initial load — backend returns all periods/weeks

			// Date range: include startDate/endDate when available (synchronized with UI)
			if (startDate && endDate) {
				initialPayload.startDate = startDate;
				initialPayload.endDate = endDate;
			}

			const allowedMetrics = await fetchKpiOverview(initialPayload);
			// [HIDDEN] SIA metric removed from display per business requirement
			const filteredMetricsResult = allowedMetrics.filter(m => m.name !== 'SIA' && m.name !== 'SIA %');
			setMetrics(filteredMetricsResult);
		} catch (err: any) {
			setError(err?.message || 'Failed to load KPI overview.');
		} finally {
			setLoading(false);
		}
	}, [showDashboardContent, locationListToken, selectedYearToken]);

	const hasFetchedInitialRef = useRef(false);
	const pendingFetchRef = useRef(false);
	const prevShowDashboardRef = useRef(showDashboardContent);

	useEffect(() => {
		if (!hasFetchedInitialRef.current) {
			hasFetchedInitialRef.current = true;
			fetchKpisInitial();
			prevShowDashboardRef.current = showDashboardContent;
			return;
		}

		// Detect tab becoming active again (showDashboardContent: false → true)
		if (showDashboardContent && !prevShowDashboardRef.current) {
			prevShowDashboardRef.current = showDashboardContent;
			fetchKpis();
			return;
		}
		prevShowDashboardRef.current = showDashboardContent;

		if (pendingFetchRef.current) {
			pendingFetchRef.current = false;
			fetchKpis();
		}
	}, [fetchKpis, fetchKpisInitial, showDashboardContent, fetchSignal]);

	const triggerFetch = useCallback(() => {
		pendingFetchRef.current = true;
		setFetchSignal(prev => prev + 1);
	}, []);

	// Filtering logic
	const filteredMetrics = useMemo(() => {
		let result = metrics;

		if (selectedCategories.length > 0) {
			result = result.filter(m => {
				const resolved = resolveKpiCategory(m.category);
				return resolved !== null && selectedCategories.includes(resolved);
			});
		}

		if (searchTerm.trim()) {
			const q = searchTerm.trim().toLowerCase();
			result = result.filter(m => {
				if (m.name.toLowerCase().includes(q)) return true;
				if (m.category.toLowerCase().includes(q)) return true;
				const resolvedCat = resolveKpiCategory(m.category);
				if (resolvedCat && resolvedCat.toLowerCase().includes(q)) return true;
				if (m.dbName && m.dbName.toLowerCase().includes(q)) return true;
				return false;
			});
		}

		return result;
	}, [metrics, searchTerm, selectedCategories]);

	const categoryCounts = useMemo(() => {
		const counts: Record<KpiCategory, number> = {
			Safety: 0,
			People: 0,
			Accuracy: 0,
			Reliability: 0,
			Cost: 0,
			Navigator: 0,
		};
		metrics.forEach(m => {
			const resolved = resolveKpiCategory(m.category);
			if (resolved) {
				counts[resolved]++;
			}
		});
		return counts;
	}, [metrics]);

	const toggleCategory = useCallback((category: KpiCategory) => {
		setSelectedCategories(prev =>
			prev.includes(category)
				? prev.filter(c => c !== category)
				: [...prev, category]
		);
	}, []);

	const topOpportunities = useMemo(
		() => filteredMetrics.filter(m => m.isUnfavorable).sort((a, b) => b.deltaMagnitude - a.deltaMagnitude),
		[filteredMetrics]
	);

	const biggestWins = useMemo(
		() => filteredMetrics.filter(m => !m.isUnfavorable).sort((a, b) => b.deltaMagnitude - a.deltaMagnitude),
		[filteredMetrics]
	);

	return {
		metrics,
		loading,
		error,
		fetchKpis,
		filteredMetrics,
		topOpportunities,
		biggestWins,
		categoryCounts,
		searchTerm,
		setSearchTerm,
		selectedCategories,
		setSelectedCategories,
		toggleCategory,
		locationListToken,
		selectedYearToken,
		selectedPeriodToken,
		selectedWeekToken,
		triggerFetch,
	};
}
